/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-return */
"use client";

import { type FC, type ReactNode, useCallback, useRef } from "react";
import { Text, usePubSub, useDebug } from "@huub-web/ui";

import { addParamsToURL } from "@huub-web/utils";

import { useAuth } from "../../hooks/useAuth";
import { APIContext, type FetchConfig } from "../../contexts";
import { convertFetchResponse } from "../../utils";
import { isProviderMismatchError } from "../../types";

export type APIProviderProps = {
  baseEndpoint: string;
  debug?: boolean;
  children: ReactNode;
};

/**
 * Generates a unique key for request deduplication
 */
const generateRequestKey = (
  method: string,
  url: string,
  params?: Record<string, any>,
): string => {
  const paramsStr = params ? JSON.stringify(params) : "";
  return `${method}:${url}:${paramsStr}`;
};

/**
 * APIProvider is a component that provides an API instance with and applied identity.
 */
export const APIProvider: FC<APIProviderProps> = ({
  baseEndpoint,
  debug = false,
  children,
}) => {
  const {
    identityToken,
    isRefreshingRef,
    unauthorized,
    handleProviderMismatch,
  } = useAuth();
  const pubSub = usePubSub();
  const { log } = useDebug();

  // Store in-flight requests to prevent duplicate calls
  const inFlightRequests = useRef<Map<string, Promise<any>>>(new Map());

  const visualDebug = (method: string, url: string) => {
    if (debug) {
      pubSub.publish("snackbar", {
        title: `[API] **${method}**`,
        body: (
          <Text variant="monospace">
            {baseEndpoint}
            {url}
          </Text>
        ),
      });
    }
  };

  /**
   * Make fetch request with authentication
   */
  const fetchWithAuth = useCallback(
    async (url: string, config: FetchConfig = {}) => {
      if (!identityToken) {
        throw new Error("No identity token");
      }

      const { params, ...fetchOptions } = config;
      const fullUrl = addParamsToURL(`${baseEndpoint}${url}`, params);

      // Handle headers carefully for FormData
      let headers: Headers;

      if (fetchOptions.headers === undefined) {
        // No headers provided, create new with just auth token
        headers = new Headers();
      } else if (fetchOptions.headers instanceof Headers) {
        // Clone the Headers instance to avoid mutating the original
        headers = new Headers(fetchOptions.headers);
      } else {
        headers = new Headers(fetchOptions.headers);
      }

      if (!headers.has("X-Id-Token")) {
        headers.set("X-Id-Token", identityToken);
      }

      try {
        const response = await fetch(fullUrl, {
          ...fetchOptions,
          headers,
        });

        if (!response.ok) {
          if (response.status === 401) {
            // Only trigger unauthorized redirect if not currently refreshing token.
            // This prevents redirect loops when API calls race with token refresh.
            // Using ref.current ensures we get the live value, not a stale closure.
            if (!isRefreshingRef.current) {
              unauthorized("invalid");
            } else {
              log(
                "yellow",
                "APIProvider",
                "401 received during token refresh - skipping unauthorized redirect",
              );
            }
          }

          // Check for provider mismatch error (403 with specific error structure)
          if (response.status === 403) {
            const errorResponse = await convertFetchResponse(response, config);
            const detail = errorResponse.data?.detail;

            if (isProviderMismatchError(detail)) {
              log(
                "yellow",
                "APIProvider",
                "Provider mismatch detected - logging out",
                detail,
              );
              handleProviderMismatch(detail);

              const error = new Error(detail.message);
              (error as any).response = errorResponse;
              throw error;
            }

            // Not a provider mismatch, throw normal error
            const error = new Error(
              errorResponse.data?.message || response.statusText,
            );
            (error as any).response = errorResponse;
            throw error;
          }

          const errorResponse = await convertFetchResponse(response, config);
          const error = new Error(
            errorResponse.data?.message || response.statusText,
          );
          (error as any).response = errorResponse;
          throw error;
        }

        return await convertFetchResponse(response, config);
      } catch (error) {
        console.error("[APIProvider]", error);
        throw error;
      }
    },
    [identityToken, baseEndpoint, unauthorized, handleProviderMismatch, log],
  );

  const $get = useCallback(
    async (url: string, config: FetchConfig = {}) => {
      // Generate unique key for request deduplication
      const requestKey = generateRequestKey("GET", url, config.params);

      // Check if there's already an in-flight request
      const existingRequest = inFlightRequests.current.get(requestKey);
      if (existingRequest) {
        log(
          "cyan",
          "APIProvider",
          "$get [DEDUPLICATED]",
          `${baseEndpoint}${url}`,
          "returning existing request",
        );
        return existingRequest;
      }

      // Log only when actually making the request
      log("blue", "APIProvider", "$get", `${baseEndpoint}${url}`, config);
      visualDebug("GET", url);

      // Create new request
      const request = fetchWithAuth(url, {
        ...config,
        method: "GET",
      }).finally(() => {
        // Delay cleanup to handle React StrictMode double-invoke
        // Keep the promise cached for 200ms to deduplicate rapid successive calls
        setTimeout(() => {
          inFlightRequests.current.delete(requestKey);
        }, 200);
      });

      // Store in-flight request
      inFlightRequests.current.set(requestKey, request);

      return request;
    },
    [fetchWithAuth, baseEndpoint, log],
  );

  const $post = useCallback(
    async (url: string, data: any, config: FetchConfig = {}) => {
      log("blue", "APIProvider", "$post", url, data, config);
      visualDebug("POST", url);

      let headers: Headers | undefined;

      if (data instanceof FormData) {
        // For FormData, don't create headers unless we have custom headers from config
        // This allows the browser to set the correct Content-Type with boundary
        if (config.headers) {
          headers = new Headers(config.headers);
        }
      } else {
        // For non-FormData requests, set Content-Type to JSON
        headers = new Headers(config.headers);
        if (!headers.has("Content-Type")) {
          headers.set("Content-Type", "application/json");
        }
      }

      return fetchWithAuth(url, {
        ...config,
        method: "POST",
        headers,
        body: data instanceof FormData ? data : JSON.stringify(data),
      });
    },
    [fetchWithAuth, baseEndpoint],
  );

  const $put = useCallback(
    async (url: string, data: any, config: FetchConfig = {}) => {
      log("blue", "APIProvider", "$put", `${baseEndpoint}${url}`, data, config);
      visualDebug("PUT", url);

      const headers = new Headers(config.headers);

      // Only set Content-Type for non-FormData requests
      if (!(data instanceof FormData) && !headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json");
      }

      return fetchWithAuth(url, {
        ...config,
        method: "PUT",
        headers,
        body: data instanceof FormData ? data : JSON.stringify(data),
      });
    },
    [fetchWithAuth, baseEndpoint],
  );

  const $patch = useCallback(
    async (url: string, data: any, config: FetchConfig = {}) => {
      log(
        "blue",
        "APIProvider",
        "$patch",
        `${baseEndpoint}${url}`,
        data,
        config,
      );
      visualDebug("PATCH", url);

      let headers: Headers | undefined;

      if (data instanceof FormData) {
        // For FormData, don't create headers unless we have custom headers from config
        // This allows the browser to set the correct Content-Type with boundary
        if (config.headers) {
          headers = new Headers(config.headers);
        }
      } else {
        // For non-FormData requests, set Content-Type to JSON
        headers = new Headers(config.headers);
        if (!headers.has("Content-Type")) {
          headers.set("Content-Type", "application/json");
        }
      }

      return fetchWithAuth(url, {
        ...config,
        method: "PATCH",
        headers,
        body: data instanceof FormData ? data : JSON.stringify(data),
      });
    },
    [fetchWithAuth, baseEndpoint],
  );

  const $delete = useCallback(
    async (url: string, config: FetchConfig = {}) => {
      log("blue", "APIProvider", "delete", `${baseEndpoint}${url}`, config);
      visualDebug("DELETE", url);

      return fetchWithAuth(url, {
        ...config,
        method: "DELETE",
      });
    },
    [fetchWithAuth, baseEndpoint],
  );

  return (
    <APIContext.Provider
      value={{
        baseEndpoint,
        $get,
        $put,
        $patch,
        $post,
        $delete,
      }}
    >
      {children}
    </APIContext.Provider>
  );
};
